import { useEffect, useMemo, useState } from "react";

import Maths from "./Maths";
import {
  api,
  type Capacite,
  type Grille,
  type Proposition,
  type Referentiel,
} from "./api";

/** Reproduit les contrôles du serveur, pour signaler pendant l'édition. */
function controler(referentiel: Referentiel, refs: string[], capacites: Set<string>) {
  const erreurs: string[] = [];
  const codes = new Set(referentiel.competences.map((c) => c.code));
  const affectees = new Set(referentiel.affectations.map((a) => a.ref));
  const utilisees = new Set(
    referentiel.affectations.flatMap((a) => a.competences),
  );

  for (const ref of refs) {
    if (!affectees.has(ref)) erreurs.push(`Question non affectée : ${ref}`);
  }
  for (const affectation of referentiel.affectations) {
    if (affectation.competences.length === 0) {
      erreurs.push(`Aucune compétence pour ${affectation.ref}`);
    }
  }
  for (const code of codes) {
    if (!utilisees.has(code)) erreurs.push(`${code} n'évalue aucune question`);
  }
  for (const competence of referentiel.competences) {
    for (const capacite of competence.capacites) {
      if (!capacites.has(capacite)) {
        erreurs.push(`${competence.code} cite une capacité inexistante : ${capacite}`);
      }
    }
  }
  return erreurs;
}

export default function Competences({ dsId }: { dsId: number }) {
  const [grille, setGrille] = useState<Grille | null>(null);
  const [capacites, setCapacites] = useState<Capacite[]>([]);
  const [referentiel, setReferentiel] = useState<Referentiel | null>(null);
  const [proposition, setProposition] = useState<Proposition | null>(null);
  const [etat, setEtat] = useState<"repos" | "appel" | "enregistrement">("repos");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    api.lireGrille(dsId).then(setGrille).catch(() => undefined);
    api.capacites(dsId).then(setCapacites).catch(() => undefined);
    // Une proposition déjà archivée se relit sans refacturer d'appel.
    api
      .relireProposition(dsId)
      .then((p) => {
        setProposition(p);
        setReferentiel(p.referentiel);
      })
      .catch(() => undefined);
  }, [dsId]);

  const parCode = useMemo(
    () => new Map(capacites.map((c) => [c.code, c])),
    [capacites],
  );
  const refs = useMemo(() => (grille?.questions ?? []).map((q) => q.ref), [grille]);
  const erreurs = useMemo(
    () =>
      referentiel
        ? controler(referentiel, refs, new Set(capacites.map((c) => c.code)))
        : [],
    [referentiel, refs, capacites],
  );

  const identifier = async () => {
    setEtat("appel");
    setMessage(null);
    try {
      const p = await api.identifierCompetences(dsId);
      setProposition(p);
      setReferentiel(p.referentiel);
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setEtat("repos");
    }
  };

  const enregistrer = async () => {
    if (!referentiel) return;
    setEtat("enregistrement");
    try {
      const bilan = await api.enregistrerCompetences(dsId, referentiel);
      setMessage(
        `${bilan.competences} compétences enregistrées, ${bilan.liens} affectations.` +
          (bilan.avertissements.length
            ? ` ${bilan.avertissements.length} avertissement(s).`
            : ""),
      );
    } catch (e) {
      setMessage((e as Error).message);
    } finally {
      setEtat("repos");
    }
  };

  const modifier = (transformer: (r: Referentiel) => Referentiel) =>
    setReferentiel((precedent) => (precedent ? transformer(precedent) : precedent));

  const basculerAffectation = (ref: string, code: string) =>
    modifier((r) => ({
      ...r,
      affectations: r.affectations.map((a) =>
        a.ref !== ref
          ? a
          : {
              ...a,
              competences: a.competences.includes(code)
                ? a.competences.filter((c) => c !== code)
                : [...a.competences, code],
            },
      ),
    }));

  if (!grille) return <p>Chargement…</p>;

  return (
    <section className="competences">
      <header className="saisie-entete">
        <div>
          <h2>Compétences — {grille.ds.titre}</h2>
          <p className="sous-titre">
            {grille.questions.length} questions
            {proposition?.origine === "archive" && " · proposition relue sans nouvel appel"}
            {proposition?.cout_dollars != null &&
              ` · appel facturé ${proposition.cout_dollars.toFixed(3)} $`}
          </p>
        </div>
        <div className="actions">
          <button type="button" onClick={identifier} disabled={etat === "appel"}>
            {etat === "appel"
              ? "Appel en cours…"
              : referentiel
                ? "Relancer l'identification (payant)"
                : "Identifier les compétences (payant)"}
          </button>
          <button
            type="button"
            onClick={enregistrer}
            disabled={!referentiel || erreurs.length > 0 || etat !== "repos"}
            title={erreurs.length > 0 ? "Corriger les erreurs d'abord" : undefined}
          >
            Enregistrer
          </button>
        </div>
      </header>

      {message && <p className="message">{message}</p>}

      {!referentiel && (
        <p className="aide">
          Aucune identification lancée. L'appel à Claude coûte environ 0,12 $ et
          n'écrit rien : vous relisez et corrigez avant d'enregistrer.
        </p>
      )}

      {erreurs.length > 0 && (
        <div className="anomalies erreurs">
          <strong>À corriger avant d'enregistrer</strong>
          <ul>
            {erreurs.slice(0, 8).map((erreur) => (
              <li key={erreur}>{erreur}</li>
            ))}
            {erreurs.length > 8 && <li>… et {erreurs.length - 8} autres</li>}
          </ul>
        </div>
      )}

      {proposition && proposition.anomalies.some((a) => a.gravite === "avertissement") && (
        <div className="anomalies avertissements">
          <strong>Points d'attention</strong>
          <ul>
            {proposition.anomalies
              .filter((a) => a.gravite === "avertissement")
              .map((a) => (
                <li key={a.message}>{a.message}</li>
              ))}
          </ul>
        </div>
      )}

      {referentiel && (
        <>
          <h3>Compétences proposées</h3>
          {referentiel.competences.map((competence) => {
            const questions = referentiel.affectations
              .filter((a) => a.competences.includes(competence.code))
              .map((a) => a.ref);
            return (
              <article key={competence.code} className="fiche-competence">
                <header>
                  <strong>{competence.code}</strong>
                  <input
                    value={competence.libelle}
                    onChange={(e) =>
                      modifier((r) => ({
                        ...r,
                        competences: r.competences.map((c) =>
                          c.code === competence.code
                            ? { ...c, libelle: e.target.value }
                            : c,
                        ),
                      }))
                    }
                    aria-label={`Libellé de ${competence.code}`}
                  />
                  <span className="chiffres">{questions.length} questions</span>
                </header>

                <p className="justification">{competence.justification}</p>

                <div className="capacites">
                  {competence.capacites.length === 0 && (
                    <span className="attente">Aucune capacité rattachée</span>
                  )}
                  {competence.capacites.map((code) => (
                    <span key={code} className="puce-capacite" title={parCode.get(code)?.libelle}>
                      {code}
                      <button
                        type="button"
                        onClick={() =>
                          modifier((r) => ({
                            ...r,
                            competences: r.competences.map((c) =>
                              c.code === competence.code
                                ? { ...c, capacites: c.capacites.filter((x) => x !== code) }
                                : c,
                            ),
                          }))
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <select
                    value=""
                    onChange={(e) => {
                      const code = e.target.value;
                      if (!code) return;
                      modifier((r) => ({
                        ...r,
                        competences: r.competences.map((c) =>
                          c.code === competence.code && !c.capacites.includes(code)
                            ? { ...c, capacites: [...c.capacites, code] }
                            : c,
                        ),
                      }));
                    }}
                  >
                    <option value="">+ capacité…</option>
                    {capacites.map((capacite) => (
                      <option key={capacite.code} value={capacite.code}>
                        {capacite.code} — {capacite.libelle.slice(0, 70)}
                      </option>
                    ))}
                  </select>
                </div>
              </article>
            );
          })}

          <h3>Affectation des questions</h3>
          <table className="affectations">
            <tbody>
              {grille.questions.map((question) => {
                const affectation = referentiel.affectations.find(
                  (a) => a.ref === question.ref,
                );
                return (
                  <tr key={question.ref}>
                    <th title={question.label}>
                      {question.ref}
                      {question.bonus && <span className="marque-bonus"> ★</span>}
                    </th>
                    <td className="apercu">
                      <Maths texte={question.label} />
                    </td>
                    <td>
                      {referentiel.competences.map((competence) => (
                        <button
                          key={competence.code}
                          type="button"
                          className={
                            affectation?.competences.includes(competence.code)
                              ? "puce active"
                              : "puce"
                          }
                          title={competence.libelle}
                          onClick={() =>
                            basculerAffectation(question.ref, competence.code)
                          }
                        >
                          {competence.code}
                        </button>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}
